import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import ReactDOM from 'react-dom/client';
import jsonData from './signals/jsonData';
import appState from './signals/appState';
// import configData from './signals/configData';
import setLanguage from './hooks/setLanguage';
import Loading from './components/Loading';
import LeftPanel from './components/LeftPanel';
import MapArea from './components/MapArea';
import RightPanel from './components/RightPanel';
import ExhibitorList from './components/ExhibitorList';
import MyFavourites from './components/MyFavourites';
import ExhibitionInfo from './components/ExhibitionInfo';
import ExhibitorInfo from './components/ExhibitorInfo';
import ExhibitorListPrint from './components/ExhibitorListPrint';
import SearchPanel from './components/SearchPanel';
import RecommendedRoutes from './components/RecommendedRoutes';
import RouteInfoPanel from './components/RouteInfoPanel';
import RouteSharePopup from './components/RouteSharePopup';
import './sass/index.sass';

const currentUrl = window.location.href;
const root = ReactDOM.createRoot(document.getElementById('root'));
let className = '';

const getPrintableLayoutSnapshot = () => {
	const mapImages = Array.from(document.querySelectorAll('.leaflet-image-layer')).map((image) => {
		const rect = image.getBoundingClientRect();
		return {
			x: rect.x,
			y: rect.y,
			width: rect.width,
			height: rect.height,
		};
	});

	const pendingImages = Array.from(document.images).filter((image) => {
		const src = image.getAttribute('src') || '';

		if (!src || src === 'data:,') return false;

		return !image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0;
	});

	return {
		mapImages,
		pendingImagesCount: pendingImages.length,
	};
};

const snapshotsEqual = (left, right) => {
	if (!left || !right) return false;
	if (left.pendingImagesCount !== right.pendingImagesCount) return false;
	if (left.mapImages.length !== right.mapImages.length) return false;

	return left.mapImages.every((image, index) => {
		const other = right.mapImages[index];
		if (!other) return false;

		return Math.abs(image.x - other.x) <= 1
			&& Math.abs(image.y - other.y) <= 1
			&& Math.abs(image.width - other.width) <= 1
			&& Math.abs(image.height - other.height) <= 1;
	});
};

const waitForPrintableLayoutStable = async () => {
	if (document.fonts?.ready) {
		try {
			await document.fonts.ready;
		} catch {
			// ignore font readiness failures
		}
	}

	let stableFrames = 0;
	let previousSnapshot = null;

	while (stableFrames < 3) {
		await new Promise((resolve) => requestAnimationFrame(resolve));

		const currentSnapshot = getPrintableLayoutSnapshot();
		const isStable = currentSnapshot.pendingImagesCount === 0 && snapshotsEqual(previousSnapshot, currentSnapshot);

		stableFrames = isStable ? stableFrames + 1 : 0;
		previousSnapshot = currentSnapshot;
	}
};

const triggerPrintWhenReady = (() => {
	let hasPrinted = false;

	return async () => {
		if (hasPrinted) return;
		hasPrinted = true;

		await waitForPrintableLayoutStable();
		window.print();
	};
})();

const MultiFloor = () => {
	const { floorInfo } = jsonData.value;

	const filteredFloorInfo = useMemo(() => {
		return floorInfo.filter((floor) => {
			return floor.floor !== '' && floor.floor != null;
		});
	}, [floorInfo]);

	const [readyCount, setReadyCount] = React.useState(0);

	useEffect(() => {
		document.body.style.overflow = 'auto';
		document.documentElement.style.overflow = 'auto';
	}, []);

	useEffect(() => {
		if (filteredFloorInfo.length > 0 && readyCount >= filteredFloorInfo.length) {
			triggerPrintWhenReady();
		}
	}, [filteredFloorInfo.length, readyCount]);

	return (
		<div id='App' className={`multi-floor${Orientation === 'landscape' ? ' landscape' : ''}`}>
			{filteredFloorInfo.map((floor, index) => {
				return <MapArea isPrint floor={floor} key={`map${index}`} onPrintReady={() => setReadyCount((count) => count + 1)} />;
			})}
			<Loading />
		</div>
	);
};

const urlParams = new URLSearchParams(window.location.search);
const Orientation = urlParams.get('orientation');

if (Orientation === 'landscape') {
	const style = document.createElement('style');
	style.textContent = '@page { size: 297mm 210mm; margin: 0; }';
	document.head.appendChild(style);
} else if (currentUrl.includes('single-floor') || currentUrl.includes('multi-floor') || currentUrl.includes('exhibitor-list')) {
	const style = document.createElement('style');
	style.textContent = '@page { size: 210mm 297mm; margin: 0; }';
	document.head.appendChild(style);
}
const Location = urlParams.get('location');
const Start = urlParams.get('start');
const booth = urlParams.get('booth');
const Navi = urlParams.get('navi');
const Floor = urlParams.get('floor');
const Lang = urlParams.get('lang');
const RouteParam = urlParams.get('route');

if (Location) localStorage.setItem('Location', Location);
if (Start) localStorage.setItem('Start', Start);

const AppWrapper = () => {
	useSignals();

	const { exhibitorInfo, floorInfo, languages } = jsonData.value;
	const naviHandledRef = useRef(false);

	// const { googleTagId } = configData.value;

	// useEffect(() => {
	// 	if (googleTagId) {
	// 		// Create first script tag for gtag config
	// 		const gtagScript1 = document.createElement('script');
	// 		gtagScript1.async = true;
	// 		gtagScript1.src = `https://www.googletagmanager.com/gtag/js?id=${googleTagId}`;
	// 		document.head.appendChild(gtagScript1);

	// 		// Create second script tag for gtag initialization
	// 		const gtagScript2 = document.createElement('script');
	// 		gtagScript2.innerHTML = `
	// 			window.dataLayer = window.dataLayer || [];
	// 			function gtag(){dataLayer.push(arguments);}
	// 			gtag('js', new Date());
	// 			gtag('config', '${googleTagId}');
	// 		`;
	// 		document.head.appendChild(gtagScript2);
	// 	}
	// }, [googleTagId]);

	useEffect(() => {
		if (booth) {
			const upperCaseBooth = booth.toUpperCase();
			const boothInfo = exhibitorInfo.find((exhibitor) => exhibitor['?支??Ⅳ'] === upperCaseBooth);
			if (boothInfo) {
				const floor = boothInfo.floor;

				appState.value = {
					...appState.value,
					ExhibitorInfo: true,
					activeFloor: floor,
					selectedBooth: upperCaseBooth,
					selectedExhibitor: upperCaseBooth,
				};
			}
		}
	}, [exhibitorInfo, floorInfo]);

	useEffect(() => {
		if (!Navi || naviHandledRef.current) return;

		const upperCaseBooth = Navi.toUpperCase();
		const boothInfo = exhibitorInfo.find((exhibitor) =>
			Object.values(exhibitor).some((value) => typeof value === 'string' && value.toUpperCase() === upperCaseBooth)
		);
		if (!boothInfo) return;

		naviHandledRef.current = true;

		appState.value = {
			...appState.value,
			ExhibitorInfo: true,
			activeFloor: boothInfo.floor,
			selectedExhibitor: upperCaseBooth,
			selectedBooth: null,
			navigation: false,
		};
	}, [exhibitorInfo, floorInfo]);

	const fetchJsonData = useCallback(() => {
		fetch('./data-common.json')
			.then((response) => response.json())
			.then((data) => {
				console.log('common data', data);
				jsonData.value = {
					...jsonData.value,
					languages: data.languages,
					basicInfo: {
						...jsonData.value.basicInfo,
						floorCount: data.basicInfo.floorCount,
						boothColor: data.basicInfo.boothColor,
					},
					floorInfo: jsonData.value.floorInfo.map((floor, index) => {
						return {
							...floor,
							floor: data.floorInfo[index]?.floor,
							booths: data.floorInfo[index]?.booths,
							sections: data.floorInfo[index]?.sections,
							paths: data.floorInfo[index]?.paths,
							floorLinks: data.floorInfo[index]?.floorLinks,
						};
					}),
				};
			})
			.catch(() => {
				alert('?臬?梁鞈??航炊');
			});
	}, []);

	useEffect(() => {
		fetchJsonData();
	}, [fetchJsonData]);

	useEffect(() => {
		if (Floor) {
			appState.value = {
				...appState.value,
				activeFloor: Number(Floor),
			};
		}
	}, [floorInfo]);

	// Handle ?route= URL parameter
	const { recommendedRoutes } = jsonData.value;
	useEffect(() => {
		if (RouteParam && recommendedRoutes && recommendedRoutes.length) {
			const routeExists = recommendedRoutes.find((r) => r['頝舐?ID'] === RouteParam);
			if (routeExists) {
				// Find first booth stop to determine initial floor
				const boothStops = recommendedRoutes
					.filter((r) => r['頝舐?ID'] === RouteParam && r['暺?憿?'] === '?支?')
					.sort((a, b) => Number(a['蝡?']) - Number(b['蝡?']));

				let targetFloor = 0;
				if (boothStops.length) {
					const boothNumber = boothStops[0]['?支??Ⅳ'];
					floorInfo.forEach((floor, index) => {
						if (floor.booths) {
							const found = floor.booths.find((b) => b.linked === boothNumber || b.exhibitor?.['?支??Ⅳ'] === boothNumber);
							if (found) targetFloor = index;
						}
					});
				}

				appState.value = {
					...appState.value,
					activeRoute: RouteParam,
					activeFloor: targetFloor,
				};
			}
		}
	}, [recommendedRoutes, floorInfo]);

	useEffect(() => {
		const browserLang = navigator.language;
		const hasLanguage = languages.find((language) => language.code === browserLang);
		const firstLanguage = languages[0];

		if (Lang) {
			setLanguage(Lang);
		} else if (hasLanguage) {
			setLanguage(browserLang);
		} else if (firstLanguage) {
			setLanguage(firstLanguage.code);
		}
	}, [languages]);

	if (currentUrl.includes('single-floor')) {
		return (
			<div id='App' className={`single-floor${Orientation === 'landscape' ? ' landscape' : ''}`}>
				<MapArea isPrint onPrintReady={triggerPrintWhenReady} />
				<Loading />
			</div>
		);
	}

	if (currentUrl.includes('multi-floor')) {
		return <MultiFloor />;
	}

	if (currentUrl.includes('exhibitor-list')) {
		return <ExhibitorListPrint onPrintReady={triggerPrintWhenReady} />;
	}

	return (
		<div id='App' className={className}>
			<LeftPanel />
			<MapArea />
			<RightPanel />
			<ExhibitorList />
			<MyFavourites />
			<ExhibitionInfo />
			<ExhibitorInfo />
			<SearchPanel />
			<RecommendedRoutes />
			<RouteInfoPanel />
			<RouteSharePopup />
			<Loading />
		</div>
	);
};

root.render(
	<div id='App' className={className}>
		<AppWrapper />
	</div>
);
